import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { Loader2, DollarSign, CheckCircle, Camera, X, CalendarIcon } from 'lucide-react';
import { GlassModal } from '@/components/ui/glass-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from '@/lib/toast';
import { useMarkAsSold } from '@/hooks/useTransactions';
import { useReceivedInterests } from '@/hooks/useItemInterests';
import { goldGradientActive } from '@/lib/gradientStyles';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { PAYMENT_METHODS } from '@/components/quickPriceEdit/paymentMethods';
import { SaleReviewPrompt } from '@/components/quickPriceEdit/SaleReviewPrompt';
import { formatUsd, formatUsdCents } from '@/lib/formatMoney';

const markAsSoldSchema = z.object({
  buyer_source: z.string(),
  sale_price: z.string().optional(),
  payment_method: z.string().optional(),
  quantity_sold: z.string().optional(),
});

type MarkAsSoldForm = z.infer<typeof markAsSoldSchema>;

interface MarkAsSoldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  itemTitle: string;
  askingPriceCents?: number | null;
  costBasisCents?: number | null;
  quantity?: number;
}

export function MarkAsSoldDialog({
  open,
  onOpenChange,
  itemId,
  itemTitle,
  askingPriceCents,
  costBasisCents,
  quantity = 1,
}: MarkAsSoldDialogProps) {
  const markAsSold = useMarkAsSold();
  const { data: receivedInterests } = useReceivedInterests();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [saleResult, setSaleResult] = useState<{
    transactionId: string;
    buyerId: string;
    buyerName: string;
  } | null>(null);
  const [salePhotoUrl, setSalePhotoUrl] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [saleDate, setSaleDate] = useState<Date>(new Date());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get accepted offers for this item
  const acceptedOffers = (receivedInterests || []).filter(
    interest => interest.item_id === itemId && interest.status === 'accepted'
  );

  const form = useForm<MarkAsSoldForm>({
    resolver: zodResolver(markAsSoldSchema),
    defaultValues: {
      buyer_source: 'other',
      sale_price: askingPriceCents ? (askingPriceCents / 100).toString() : '',
      payment_method: '',
      quantity_sold: '1',
    },
  });

  const watchSalePrice = form.watch('sale_price');
  const watchQuantitySold = form.watch('quantity_sold');
  const quantitySoldNum = Math.min(
    Math.max(1, parseInt(watchQuantitySold || '1', 10) || 1),
    quantity,
  );

  // Calculate profit/loss (cost basis is per-unit → multiply by qty sold)
  const calculateProfit = () => {
    if (!watchSalePrice || costBasisCents == null) return null;
    const salePriceCents = Math.round(Number(watchSalePrice) * 100);
    const totalCost = costBasisCents * quantitySoldNum;
    return (salePriceCents - totalCost) / 100;
  };

  const profit = calculateProfit();

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const { processSelectedFiles, resetFileInput } = await import('@/lib/photoInputUtils');
    const [file] = await processSelectedFiles(target.files, { multiple: false });
    resetFileInput(target);
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const fileName = `${user.id}/sale-${itemId}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('listing-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('listing-images')
        .getPublicUrl(fileName);

      setSalePhotoUrl(publicUrl);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const removePhoto = () => {
    setSalePhotoUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data: MarkAsSoldForm) => {
    setIsSubmitting(true);

    try {
      let buyerId: string | undefined;
      let buyerName: string | undefined;
      let salePriceCents: number | undefined;

      // If from accepted offer, use that buyer and price
      if (data.buyer_source !== 'other') {
        const offer = acceptedOffers.find(o => o.id === data.buyer_source);
        if (offer) {
          buyerId = offer.sender_id;
          buyerName = offer.sender?.username || 'CardSimp User';
          salePriceCents = offer.offer_cents || undefined;
        }
      }

      // Override with manual price if provided
      if (data.sale_price) {
        salePriceCents = Math.round(Number(data.sale_price) * 100);
      }

      const result = await markAsSold.mutateAsync({
        itemId,
        buyerId,
        salePriceCents,
        quantitySold: quantitySoldNum,
        paymentMethod: data.payment_method || undefined,
        salePhotoUrl: salePhotoUrl || undefined,
        saleDate,
      });

      // If there's a known buyer, show review prompt
      if (buyerId && result?.transaction) {
        setSaleResult({
          transactionId: result.transaction.id,
          buyerId,
          buyerName: buyerName || 'the buyer',
        });
        setShowReviewPrompt(true);
      } else {
        toast.success('Item marked as sold!', {
          description: 'Your item has been removed from the marketplace.',
        });
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error marking as sold:', error);
      toast.error('Failed to mark as sold');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Review prompt after successful sale — shared with QuickPriceEditDialog
  if (showReviewPrompt && saleResult) {
    return (
      <SaleReviewPrompt
        open={open}
        transactionId={saleResult.transactionId}
        buyerId={saleResult.buyerId}
        buyerName={saleResult.buyerName}
        onDismiss={() => {
          setShowReviewPrompt(false);
          setSaleResult(null);
          onOpenChange(false);
        }}
      />
    );
  }

  return (
    <GlassModal
      open={open}
      onOpenChange={onOpenChange}
      title="Mark as Sold"
      description={`Confirm the sale of "${itemTitle}"`}
    >
      <div className="space-y-2 text-left pb-2">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-success" />
          Mark as Sold
        </h2>
        <p className="text-sm text-muted-foreground">
          Confirm the sale of "{itemTitle}"
        </p>
      </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {acceptedOffers.length > 0 && (
              <FormField control={form.control} name="buyer_source" render={({ field }) => (
                <FormItem>
                  <FormLabel>Sold to</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select buyer" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {acceptedOffers.map(offer => (
                        <SelectItem key={offer.id} value={offer.id}>
                          {offer.sender?.username || 'CardSimp User'}
                          {offer.offer_cents && ` - ${formatUsdCents(offer.offer_cents)}`}
                        </SelectItem>
                      ))}
                      <SelectItem value="other">Someone else / External sale</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Select the buyer to enable them to leave a review
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            {/* Sale Details Section */}
            <div className="space-y-3">
              {/* Quantity Sold (only if multi-unit) */}
              {quantity > 1 && (
                <FormField control={form.control} name="quantity_sold" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantity Sold</FormLabel>
                    <FormControl>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          min={1}
                          max={quantity}
                          className="w-24"
                          {...field}
                        />
                        <span className="text-sm text-muted-foreground">
                          of {quantity} available
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              )}

              {/* Final Sale Price */}
              <FormField control={form.control} name="sale_price" render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Final Sale Price{quantitySoldNum > 1 ? ' (total)' : ''}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*\.?[0-9]*"
                        {...PRICE_INPUT_PROPS}
                        placeholder="0.00"
                        className="pl-9"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value.replace(/[^0-9.]/g, ''))}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              {/* Sale Date */}
              <div className="space-y-2">
                <FormLabel>Sale Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !saleDate && 'text-muted-foreground',
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {saleDate ? format(saleDate, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={saleDate}
                      onSelect={(date) => date && setSaleDate(date)}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Payment Method + Sale Photo Row */}
              <div className="flex gap-3">
                {/* Payment Method */}
                <FormField control={form.control} name="payment_method" render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-xs text-muted-foreground">Payment Method</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PAYMENT_METHODS.map(method => {
                          const Icon = method.icon;
                          return (
                            <SelectItem key={method.value} value={method.value}>
                              <span className="flex items-center gap-2">
                                <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                                {method.label}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />

                {/* Sale Photo */}
                <div className="flex-1">
                  <FormLabel className="text-xs text-muted-foreground mb-2 block">Sale Photo</FormLabel>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.heic,.heif,.dng"
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                  {salePhotoUrl ? (
                    <div className="relative h-9 w-full rounded-md border border-border overflow-hidden group">
                      <img
                        src={salePhotoUrl}
                        alt="Sale proof"
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 w-full"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingPhoto}
                    >
                      {isUploadingPhoto ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Camera className="w-4 h-4 mr-1.5" />
                          <span className="text-xs">Add Photo</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              <p className="text-[11px] text-muted-foreground/70">
                Optional — helps track your sales history
              </p>
            </div>

            {/* Profit/Loss Display */}
            {profit !== null && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-black/30 border border-white/[0.08]">
                <span className="text-sm text-muted-foreground">
                  {quantitySoldNum > 1 ? `Profit/Loss (${quantitySoldNum} items):` : 'Profit/Loss:'}
                </span>
                <span className={cn(
                  'font-bold',
                  profit > 0 ? 'text-success' : profit < 0 ? 'text-destructive' : 'text-muted-foreground',
                )}>
                  {profit > 0 ? '+' : ''}{formatUsd(profit)}
                </span>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className={cn('flex-1', goldGradientActive)}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Confirm Sale'
                )}
              </Button>
            </div>
          </form>
        </Form>
    </GlassModal>
  );
}
